import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Ban, CheckCircle2, Dumbbell, FileText, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD_GRADIENT = 'linear-gradient(135deg, #FFD700 0%, #F59E0B 100%)';

const BrandMark = () => (
  <div className="mb-10 flex items-center justify-center gap-3" data-testid="rechnung-status-brand">
    <div
      className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg"
      style={{ background: GOLD_GRADIENT, boxShadow: '0 0 28px rgba(255, 215, 0, 0.18)' }}
    >
      <Dumbbell className="text-black" size={24} />
    </div>
    <span className="text-3xl font-extrabold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
      STRONG<span className="text-yellow-400">FIT</span>
    </span>
  </div>
);

const useQueryParam = (key) => {
  const [value] = useState(() => new URLSearchParams(window.location.search).get(key) || '');
  return value;
};

const RechnungStatusPage = () => {
  const nr = useQueryParam('nr');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/public/invoice-status`, { params: { nr } });
        if (active) setData(res.data);
      } catch (e) {
        if (active) setData({ found: false, status: 'unknown' });
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [nr]);

  const status = data?.status || 'unknown';
  const amountLabel =
    data?.amount != null ? `${data.currency || 'CHF'} ${Number(data.amount).toFixed(2)}` : null;

  let icon = <FileText className="text-yellow-400" size={40} />;
  let iconWrap = 'border-yellow-400/30 bg-yellow-400/10';
  let title = 'Rechnung';
  let message = 'Der Status dieser Rechnung konnte nicht ermittelt werden.';

  if (status === 'cancelled') {
    icon = <Ban className="text-red-400" size={40} />;
    iconWrap = 'border-red-400/30 bg-red-400/10';
    title = 'Rechnung storniert';
    message = 'Diese Rechnung wurde storniert und muss nicht mehr bezahlt werden. Es entstehen dir keine Kosten. Bei Fragen melde dich gerne bei uns.';
  } else if (status === 'paid' || status === 'bezahlt') {
    icon = <CheckCircle2 className="text-green-400" size={40} />;
    iconWrap = 'border-green-400/30 bg-green-400/10';
    title = 'Zahlung erhalten';
    message = 'Vielen Dank! Deine Zahlung ist bei uns eingegangen. Diese Rechnung ist vollständig beglichen.';
  } else if (['pending', 'sent', 'overdue'].includes(status)) {
    icon = <FileText className="text-yellow-400" size={40} />;
    iconWrap = 'border-yellow-400/30 bg-yellow-400/10';
    title = 'Rechnung offen';
    message = 'Diese Rechnung ist noch offen. Du kannst sie über den untenstehenden Button bequem online bezahlen.';
  } else if (data && data.found === false) {
    title = 'Rechnung nicht gefunden';
    message = 'Zu diesem Link konnten wir keine Rechnung finden. Bei Fragen melde dich gerne bei uns.';
  }

  return (
    <main
      className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-4 py-10 text-white sm:px-8"
      data-testid="rechnung-status-page"
    >
      <div className="w-full max-w-lg">
        <BrandMark />
        <Card
          className="w-full rounded-lg border-[#2a2a2a] bg-[#1a1a1a] text-center text-white shadow-2xl animate-fade-up"
          data-testid="rechnung-status-card"
        >
          <CardContent className="px-6 py-12 sm:px-10">
            {loading ? (
              <div className="flex flex-col items-center gap-4 py-6" data-testid="rechnung-status-loading">
                <Loader2 className="animate-spin text-yellow-400" size={36} />
                <p className="text-sm text-zinc-400">Rechnung wird geprüft…</p>
              </div>
            ) : (
              <>
                <div className={`mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border ${iconWrap}`}>
                  {icon}
                </div>
                <h1 className="text-4xl font-extrabold sm:text-5xl" data-testid="rechnung-status-title">
                  {title}
                </h1>
                {data?.invoice_number && (
                  <p className="mt-3 text-sm font-semibold uppercase tracking-wide text-zinc-500" data-testid="rechnung-status-number">
                    Rechnung {data.invoice_number}
                    {amountLabel ? ` · ${amountLabel}` : ''}
                  </p>
                )}
                <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base" data-testid="rechnung-status-message">
                  {message}
                </p>

                {['pending', 'sent', 'overdue'].includes(status) && data?.payment_link && (
                  <Button
                    onClick={() => {
                      window.location.href = data.payment_link;
                    }}
                    className="mt-8 h-12 w-full rounded-lg font-bold uppercase text-black transition-transform duration-200 hover:scale-[1.01] active:scale-[0.98]"
                    style={{ background: GOLD_GRADIENT }}
                    data-testid="rechnung-status-pay-button"
                  >
                    Jetzt bezahlen
                  </Button>
                )}

                <p className="mt-8 text-xs text-zinc-500">StrongFit · Bei Fragen sind wir gerne für dich da.</p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default RechnungStatusPage;
